import React, { useCallback, useRef, useState } from 'react'

import classNames from 'classnames'
import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { Trans, useTranslation } from 'react-i18next'
import { createApi } from 'unsplash-js'

import Button from '@components/shared/Button'
import ExternalLink from '@components/shared/ExternalLink'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NFIcon from '@components/shared/NFIcon'
import Input from '@components/shared/form/Input'
import { useAsyncEffect } from '@hooks/index'
import useDebounce from '@hooks/useDebounce'
import { useDialogStore } from '@stores/dialog.store'
import { useSession } from '@stores/globalData/globalData.store'

import type { Photos } from 'unsplash-js/src/methods/search/types/response'

const IMAGES_PER_PAGE = 21
const MIN_NUM_CHAR = 3
const DEBOUNCE_DELAY = 500
const INITIAL_QUERY = 'music'

interface Props {
  onComplete: (url: string, blurHash: string | null) => Promise<void>
}

const ImagePicker: React.FC<Props> = ({ onComplete }) => {
  const { t } = useTranslation()
  const session = useSession()
  const closeDialog = useDialogStore((s) => s.closeDialog)

  const unsplash = useRef(
    createApi({
      apiUrl: `https://${session.community.hostname}/unsplash`,
    })
  )

  const [submitting, setSubmitting] = useState(false)
  const [selectedId, setSelected] = useState('')
  const [searching, setSearching] = useState(true)
  const [page, setPage] = useState(1)
  const [imageResponse, setImageResponse] = useState<Photos>({
    results: [],
    total: 0,
    total_pages: 0,
  })

  const [query, setQuery] = useState(INITIAL_QUERY)
  const [prevQuery, setPrevQuery] = useState(query)
  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY)

  const scrollRef = useBottomScrollListener<HTMLDivElement>(() => {
    if (searching || imageResponse.total_pages <= page) return

    setPage((currPage) => currPage + 1)
  })

  const searchPhotos = useCallback(
    async (term: string, pageNum: number) => {
      const { response } = await unsplash.current.search.getPhotos({
        query: term,
        page: pageNum,
        perPage: IMAGES_PER_PAGE,
      })
      return response ?? null
    },
    [unsplash]
  )

  useAsyncEffect(
    async (isActive) => {
      if (debouncedQuery.length < MIN_NUM_CHAR) return

      // If query has changed and not on the first page, just change the page number
      if (debouncedQuery !== prevQuery && page !== 1) {
        setPage(1)
        return
      }

      setSearching(true)
      const response = await searchPhotos(debouncedQuery, page)

      // Prevent setting state if component has unmounted
      if (!isActive()) return

      setSearching(false)
      if (!response) return

      // New set of images on first page
      if (page === 1) {
        setImageResponse(response)
      }
      // Otherwise, append images to list
      else {
        setImageResponse((prevResp) => ({
          ...prevResp,
          results: [...prevResp.results, ...response.results],
        }))
      }
      setPrevQuery(debouncedQuery)
    },
    [page, debouncedQuery]
  )

  return (
    <div className="flex flex-col">
      <div className="relative w-full">
        <Input
          name="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('placeholder.searchForFreePhotos')}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          inputClassName="pl-8"
        />
        <div className="absolute top-0 bottom-0 left-2 p-1 flex flex-row items-center mb-2">
          <NFIcon icon="search" className="text-base" />
        </div>
      </div>
      <div className="overflow-y-auto max-h-[450px] text-center mt-2" ref={scrollRef}>
        {imageResponse.results.map((image) => (
          <div key={image.id} className="relative inline-block mb-2 mr-2.5">
            <button
              type="button"
              className="flex flex-row items-center justify-center relative w-[234px] h-[160px] overflow-hidden"
              onClick={() => setSelected(image.id)}>
              <img
                src={image.urls.small}
                alt={image.alt_description ?? ''}
                className={classNames('w-full h-full object-cover', {
                  'brightness-75': selectedId === image.id,
                })}
              />
              {selectedId === image.id && (
                <div className="inset-0 absolute flex flex-row items-center justify-center">
                  <NFIcon icon="ok-sign" className="text-h1 text-white p-1" />
                </div>
              )}
            </button>
            <div className="text-sm text-right w-full mt-1">
              <ExternalLink
                href={`${image.user.links.html}?utm_source=noteflight&utm_medium=referral&utm_campaign=api-credit`}>
                {image.user.name}
              </ExternalLink>
              <span className="mx-1">/</span>
              <ExternalLink href="https://www.unsplash.com?utm_source=noteflight&utm_medium=referral&utm_campaign=api-credit">
                Unsplash
              </ExternalLink>
            </div>
          </div>
        ))}
        {imageResponse.results.length === 0 && (
          <div className="w-full flex flex-col items-center justify-center py-8">
            <p>{t('content.noImages')}</p>
          </div>
        )}
        {imageResponse.total_pages > page && <LoadingSpinner />}
      </div>
      <div className="mt-4 flex flex-row w-full items-center">
        <div className="flex-1">
          <p className="max-w-[430px] text-sm text-gray-text dark:text-gray-text-dm">
            <Trans i18nKey="content.imagePickerCopyright">
              Noteflight is providing a feed of images from&nbsp;
              <ExternalLink href="https://unsplash.com/">Unsplash.com</ExternalLink>
              &nbsp;as a resource. Please do not select images of brands or recognizable faces.
            </Trans>
          </p>
        </div>
        <Button variant="secondary" onClick={() => closeDialog('image_picker')}>
          {t('action.cancel')}
        </Button>
        <Button
          variant="primary"
          className="ml-3"
          disabled={!selectedId || submitting}
          onClick={async () => {
            const selectedImage = imageResponse.results.find((image) => image.id === selectedId)
            if (!selectedImage) return

            // Make a call to the download_location unsplash link so they can give some credit to the photographer! (Unsplash required that)
            // We don't want to do anything with that, so no callback needed.
            unsplash.current.photos.trackDownload({
              downloadLocation: selectedImage.links.download_location,
            })

            setSubmitting(true)
            await onComplete(selectedImage.urls.full, selectedImage.blur_hash)
            setSubmitting(false)
          }}>
          {t('action.next')}
        </Button>
      </div>
    </div>
  )
}

export default ImagePicker
